import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, Timer } from 'lucide-react';

const SYNONYM_PAIRS = [
  { word: 'Happy', synonyms: ['Joyful', 'Glad', 'Cheerful'], wrong: ['Sad', 'Angry', 'Scared'] },
  { word: 'Big', synonyms: ['Large', 'Huge', 'Enormous'], wrong: ['Small', 'Tiny', 'Little'] },
  { word: 'Fast', synonyms: ['Quick', 'Rapid', 'Swift'], wrong: ['Slow', 'Lazy', 'Dull'] },
  { word: 'Smart', synonyms: ['Clever', 'Intelligent', 'Bright'], wrong: ['Foolish', 'Silly', 'Dull'] },
  { word: 'Brave', synonyms: ['Courageous', 'Bold', 'Fearless'], wrong: ['Coward', 'Timid', 'Weak'] },
  { word: 'Beautiful', synonyms: ['Gorgeous', 'Lovely', 'Stunning'], wrong: ['Ugly', 'Plain', 'Dull'] },
  { word: 'Angry', synonyms: ['Furious', 'Enraged', 'Irate'], wrong: ['Calm', 'Happy', 'Peaceful'] },
  { word: 'Old', synonyms: ['Ancient', 'Elderly', 'Aged'], wrong: ['New', 'Young', 'Fresh'] },
  { word: 'Begin', synonyms: ['Start', 'Commence', 'Initiate'], wrong: ['End', 'Finish', 'Stop'] },
  { word: 'Difficult', synonyms: ['Hard', 'Tough', 'Challenging'], wrong: ['Easy', 'Simple', 'Effortless'] },
];

const SynonymSprint = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [index, setIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);

  const generateOptions = (idx) => {
    const pair = SYNONYM_PAIRS[idx];
    const correct = pair.synonyms[Math.floor(Math.random() * pair.synonyms.length)];
    const wrongs = pair.wrong.slice(0, 3);
    setOptions([correct, ...wrongs].sort(() => Math.random() - 0.5));
    return correct;
  };

  const [correctAnswer, setCorrectAnswer] = useState(() => {
    const pair = SYNONYM_PAIRS[0];
    return pair.synonyms[0];
  });

  useEffect(() => {
    const c = generateOptions(0);
    setCorrectAnswer(c);
  }, []);

  useEffect(() => {
    if (timeLeft <= 0) { setGameOver(true); return; }
    const t = setInterval(() => setTimeLeft(v => v-1), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const handleAnswer = (opt) => {
    if (feedback || gameOver) return;
    const pair = SYNONYM_PAIRS[index];
    const isCorrect = pair.synonyms.includes(opt);
    if (isCorrect) { setScore(s => s+10); setFeedback({ correct: true }); }
    else setFeedback({ correct: false });
    setTimeout(() => {
      const nextIdx = (index + 1) % SYNONYM_PAIRS.length;
      setIndex(nextIdx);
      const c = generateOptions(nextIdx);
      setCorrectAnswer(c);
      setFeedback(null);
    }, 500);
  };

  if (gameOver) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">🏃</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Sprint Over!</h2>
          <p className="text-6xl font-black text-fuchsia-500 mb-2">{score}</p>
          <p className="text-xl text-[#64748B] mb-8">points scored</p>
          <div className="space-y-3">
            <button onClick={() => { setScore(0); setIndex(0); setTimeLeft(45); setGameOver(false); setFeedback(null); generateOptions(0); }} className="w-full py-4 bg-fuchsia-500 text-white rounded-2xl font-bold border-b-4 border-fuchsia-700 active:border-b-0 active:translate-y-1 transition-all">Sprint Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 to-pink-50" data-testid="synonym-sprint-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3">
            <div className={`px-4 py-2 rounded-2xl font-bold flex items-center gap-2 text-white ${timeLeft <= 10 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}><Timer className="w-5 h-5" />{timeLeft}s</div>
            <span className="bg-fuchsia-500 text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center">
          <ArrowRightLeft className="w-12 h-12 text-fuchsia-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-[#1E293B] mb-2">Find the Synonym!</h2>
          <p className="text-5xl font-black text-fuchsia-500 mb-8">{SYNONYM_PAIRS[index].word}</p>
          <div className="grid grid-cols-2 gap-4">
            {options.map((opt, i) => (
              <button key={i} onClick={() => handleAnswer(opt)} data-testid={`synonym-${i}`}
                className={`p-5 rounded-2xl text-xl font-bold border-2 transition-all ${
                  feedback && SYNONYM_PAIRS[index].synonyms.includes(opt) ? 'bg-green-100 border-green-500 text-green-700' :
                  feedback ? 'bg-slate-50 border-slate-200 text-slate-400' :
                  'bg-white border-slate-200 hover:border-fuchsia-400 hover:bg-fuchsia-50 text-[#1E293B] active:scale-95'
                }`}>{opt}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SynonymSprint;
